import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GitHubAppConnectionSummary, GitHubAppRepositories, GitHubRepository } from "@octopusdeploy/octopus-server-client";
import { useEffect, useState } from "react";

export interface GitHubConnectionRepositorySelectorState {
    connection: GitHubAppConnectionSummary | undefined;
    repository: GitHubRepository | undefined;

    connections: GitHubAppConnectionSummary[] | undefined;
    refreshConnections: () => Promise<void>;

    repositories: GitHubAppRepositories | undefined;
    refreshRepositories: () => void;

    setConnection(connectionId: string | undefined): void;
    setRepository(repositoryId: string | undefined): void;
}

export function useGitHubConnectionRepositorySelector(
    connections: GitHubAppConnectionSummary[] | undefined,
    refreshConnections: () => Promise<void>,
    connectionId: string | undefined,
    url: string | undefined,
    onChange: (connectionId: string | undefined, url: string | undefined) => void
): GitHubConnectionRepositorySelectorState {
    const [selectedConnection, setSelectedConnection] = useState<GitHubAppConnectionSummary | undefined>(undefined);
    const [selectedRepository, setSelectedRepository] = useState<GitHubRepository | undefined>(undefined);

    const { result: repositories, refetch: refreshRepositories } = useQuery(
        async (repository) => {
            if (connectionId) {
                const newSelectedConnection = connections?.find((value) => value.Id === connectionId);
                if (newSelectedConnection) {
                    return await repository.GitHubApp.getRepositoriesForConnection(newSelectedConnection.Id);
                }
            }
        },
        [connectionId, connections],
        "Load GitHub repositories for connection"
    );

    useEffect(() => {
        setSelectedConnection(connections?.find((value) => value.Id === connectionId));
        setSelectedRepository(repositories?.Repositories.find((value) => value.GitUrl === url));
    }, [connectionId, url, connections, repositories?.Repositories]);

    const setConnection = async (newConnectionId: string | undefined) => {
        // If the connection hasn't changed (someone's just re-selecting the current one), we don't want to clear the repository.
        if (newConnectionId !== connectionId) {
            onChange(newConnectionId, undefined);
        }
    };

    const setRepository = async (newRepositoryId: string | undefined) => {
        const newRepository = repositories?.Repositories.find((value) => value.RepositoryId === newRepositoryId);
        onChange(connectionId, newRepository?.GitUrl);
    };

    return {
        connection: selectedConnection,
        repository: selectedRepository,
        connections: connections,
        refreshConnections,
        repositories: repositories ?? undefined, // Coalesce null to undefined for consistency
        refreshRepositories,
        setConnection,
        setRepository,
    };
}
